import { labels as additionalAttributes } from "components/Campaign/CampaignConditions/AdditionalAttribute";
import {
  array,
  boolean,
  lazy,
  mixed,
  number,
  object,
  string,
  TestContext,
} from "yup";

type TestContextExtended = {
  from: {
    value: { isEditing: boolean; type: string };
  }[];
};

// For validation to work correctly, the fields in the schema must match the fields in the getAchievementInfoFields method
export const getAchievementInfoScheme = (message: any) =>
  object()
    .shape({
      translations: array(
        object().shape({
          name: string().required(message({ id: "thisValueShouldNotBeBlank" })),
        }),
      ),
      activity: object().shape({
        operator: string(),
        data: mixed().when("operator", {
          is: (v: string) => v !== "always_available",
          then: () =>
            lazy((value) =>
              Array.isArray(value)
                ? array()
                    .min(1, message({ id: "thisValueShouldNotBeBlank" }))
                    .required()
                : typeof value === "object" && value !== null
                  ? object().shape({
                      from: string().required(
                        message({ id: "thisValueShouldNotBeBlank" }),
                      ),
                      to: string().required(
                        message({ id: "thisValueShouldNotBeBlank" }),
                      ),
                    })
                  : string().required(
                      message({ id: "thisValueShouldNotBeBlank" }),
                    ),
            ),
        }),
      }),
      limit: object().shape({
        interval: object().shape({
          type: string(),
        }),
        value: number().when("interval.type", {
          is: (v: string) => v !== "noLimit",
          then: () =>
            number().required(message({ id: "thisValueShouldNotBeBlank" })),
        }),
      }),
    })
    .required();

export const getAchievementRulesScheme = (message: any) => {
  const attributes = Object.keys(additionalAttributes);

  return object().shape({
    rules: array(
      object({
        conditions: array(
          object().shape({
            isEditing: boolean().test(
              "isNotEditing",
              message({ id: "theValueShouldBeSaved" }),
              (value) => value === false,
            ),
            attribute: string(),
            param: mixed().when("attribute", {
              is: (v: string) => attributes.includes(v),
              then: (schema) =>
                schema.required(message({ id: "thisValueShouldNotBeBlank" })),
            }),
            data: mixed()
              .when("attribute", {
                is: (v: string) => attributes.includes(v),
                then: (schema) =>
                  schema.required(message({ id: "thisValueShouldNotBeBlank" })),
              })
              .nullable(),
          }),
        ),
        completeRule: object().shape({
          isEditing: boolean().test(
            "isNotEditing",
            message({ id: "theValueShouldBeSaved" }),
            (value) => value === false,
          ),
          period: object().shape({
            type: string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
            consecutive: mixed().test(
              "isConsecutive",
              message({ id: "thisValueShouldNotBeBlank" }),
              function (value) {
                const { from } = this as TestContext & TestContextExtended;
                // parent1 covers the scope of the period obj
                // parent2 covers the scope of the completeRule obj
                const [parent1, parent2] = from;

                return (
                  parent1.value?.type === "overall" ||
                  parent1.value?.type === "last_day" ||
                  (value !== 0 && value !== undefined) ||
                  parent2.value?.isEditing === true
                );
              },
            ),
            value: number().test(
              "isValue",
              message({ id: "thisValueShouldNotBeBlank" }),
              function (value) {
                const { from } = this as TestContext & TestContextExtended;
                const [parent1, parent2] = from;

                return (
                  parent1.value?.type !== "last_day" ||
                  (value !== 0 && value !== undefined) ||
                  parent2.value?.isEditing === true
                );
              },
            ),
          }),
          periodGoal: mixed().test(
            "isZero",
            message({ id: "theValueShouldBeGreaterThanZero" }),
            (value, context) =>
              (value !== 0 && value !== undefined) ||
              context.parent?.isEditing === true,
          ),
          onlyUniqueAttributeValues: boolean(),
          uniqueAttribute: string().test(
            "isUniqueAttributeSelected",
            message({ id: "thisValueShouldNotBeBlank" }),
            function (value) {
              const { from } = this as TestContext & TestContextExtended;
              const [parent1] = from;

              return (
                (value && value.length > 0) ||
                !parent1.value?.onlyUniqueAttributeValues
              );
            },
          ),
        }),
        limit: object().shape({
          interval: object().shape({
            type: string(),
          }),
          value: number().when("interval.type", {
            is: (v: string) => v !== "noLimit",
            then: () =>
              number().required(message({ id: "thisValueShouldNotBeBlank" })),
          }),
        }),
        trigger: string(),
        event: string().when("trigger", {
          is: (v: string) => v === "custom_event",
          then: () =>
            string().required(message({ id: "thisValueShouldNotBeBlank" })),
        }),
        aggregation: object().shape({
          type: string(),
          rule: string().when("type", {
            is: (v: string) => v === "expression",
            then: () =>
              string().required(message({ id: "thisValueShouldNotBeBlank" })),
          }),
        }),
      }),
    ).required(),
  });
};
